import { ChangeDetectorRef, Directive, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { parseNumber } from '@df/util/fn/parse-number';

export interface IFileInputEvent {
    file?: File;
    files?: File[];
    error?: Error;
    component: FileInputComponentDef;
    errorFiles?: IFileInputErrorFile[];
}

export interface IFileInputErrorFile {
    file: File;
    error: Error;
}

@Directive()
export class FileInputComponentDef {
    @ViewChild('input')
    inputElementRef?: ElementRef<HTMLInputElement>;

    private _accept?: string;

    /**
     * Max file size
     */
    @Input()
    maxSize: string | number = '5MB';

    @Input()
    inputId?: string;

    /**
     * Max file size
     */
    @Input()
    variant?: string;

    /**
     * Accepted file formats
     */
    @Input()
    set accept(accept: string | undefined) {
        this._accept =
            accept &&
            accept
                .toLowerCase()
                .replace(/\s/g, '')
                .split(',')
                .filter(i => !!i)
                .map(extension => {
                    if (extension.indexOf('/') > -1 || extension[0] === '.') {
                        return extension;
                    }
                    return `.${extension}`;
                })
                .join(',');
    }

    get accept(): string | undefined {
        return this._accept;
    }

    /**
     * File change event listener
     */
    @Output()
    fileChange = new EventEmitter<IFileInputEvent>();

    constructor(private changeDetectorRef: ChangeDetectorRef) { }

    /**
     * Called on component initiation
     * Binds change event to child input (note that ng-change does not work with file input)
     */
    onChange(event: Event) {
        const input = <HTMLInputElement>event.target;
        const files = [...(input.files || [])];

        if (!files?.length) {
            return;
        }

        const maxSize = this.maxSize ? (typeof this.maxSize === 'string' ? parseNumber(this.maxSize) : this.maxSize) : null;

        const validFiles: File[] = [];

        const errorFiles: IFileInputErrorFile[] = [];

        files.forEach(file => {
            // Check file size
            if (maxSize && file.size > maxSize) {
                errorFiles.push({ file, error: new Error(`Max file size is ${this.maxSize}`) });
            } else {
                // Prevent from uploading invalid filetypes
                const type = file.type?.toLowerCase();
                if (type && !this.isTypeAllowed(type)) {
                    errorFiles.push({ file, error: new Error('Invalid file type') });
                } else {
                    validFiles.push(file);
                }
            }
        });

        if (!validFiles.length) {
            this.reset();
        }

        // Emit single file or array of files based on multiple property
        this.emit(validFiles.length === 0 ? errorFiles[0].error : undefined, validFiles[0], validFiles, errorFiles);

        this.detectChanges();
    }

    private emit(error?: Error, file?: File, files?: File[], errorFiles?: IFileInputErrorFile[]): void {
        this.fileChange.emit({ error, file, files, component: this, errorFiles });
    }

    reset() {
        if (this.inputElementRef?.nativeElement) {
            this.inputElementRef.nativeElement.value = '';
        }
        this.detectChanges();
    }

    private isTypeAllowed(type: string): boolean {
        if (this.accept) {
            return !!this.accept.split(',').find(extension => {
                //if extension doesn't contain '/', remove dot from extension and check if bare form exists.
                if (extension.indexOf('/') === -1) {
                    return type.indexOf(extension.replace('.', '')) !== -1;
                } else {
                    if (extension.indexOf('/*') > -1) {
                        //if it contains "/*", look for general type (like "image/")
                        return type.indexOf(extension.slice(0, extension.indexOf('*'))) === 0;
                    } else {
                        //if it does contain "/" but no "/*", just do 1:1 compare
                        return extension === type;
                    }
                }
            });
        }
        return true;
    }

    private detectChanges(): void {
        this.changeDetectorRef.detectChanges();
    }
}
